/**
 * Tutor Buddy — a floating, emotion-aware mascot inspired by Emergent's Wingman.
 *
 * Cost: ₹0 (pure SVG + CSS + framer-motion + canvas-confetti).
 *
 * Behaviour by subscription tier:
 *   - Paid tiers (basic_lifetime / growth / premium / elite): full library, full
 *     emotional range, drag-to-reposition, all events fire.
 *   - Free Basic: limited bubbles only, occasional "upgrade to unlock me fully"
 *     nudges, no eye-tracking or drag (saves CPU on low-end devices).
 *
 * Position: bottom-28 right-5 — sits ABOVE the Emergent watermark and clears
 * a typical chat-input bar. Speech bubble extends LEFT so it never overlaps
 * the watermark.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useNavigate } from 'react-router-dom';
import { X, Minimize2, Lock, Gift, Users, MessageCircleHeart, MessageSquareText, NotebookPen, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { BuddyAvatar, BUDDY_ROSTER } from '@/components/BuddyRoster';
import RosterPicker from '@/components/RosterPicker';
import SafeSpacePanel from '@/components/SafeSpacePanel';
import FeedbackModal from '@/components/FeedbackModal';
import BuddyMenu from '@/components/BuddyMenu';
import NotesPad from '@/components/notes/NotesPad';
import { NOTE_EVENTS, fetchTonight } from '@/lib/notes';

const TutorBuddyContext = createContext(null);

const PAID_TIERS = new Set(['basic_lifetime', 'growth', 'premium', 'elite']);

// Free-tier library — small, intentionally limited to nudge upgrades.
const FEEDBACK_LINES = [
  "You're part of the Gurukul Veda family \uD83D\uDC9B Could you share a little feedback so we can make things better for you?",
  "Mind telling us how we're doing? Your feedback helps us improve \u2014 and you're family here! \uD83C\uDF31",
  "Psst\u2026 a tiny bit of feedback from you would mean the world. Help us make Gurukul Veda even better? \uD83D\uDC9B",
  "We're building this for you. Tap below and tell us what to improve \u2014 every word helps! \u2728",
];
const FREE_LIBRARY = {
  idle: [
    "I'm right here whenever you need me.",
    "Ask me even the silliest doubt \u2014 I won't judge.",
    "Stuck? Type your question. I\u2019ll meet you halfway.",
    "Try the camera button \u2014 snap a problem and I\u2019ll solve it with you.",
  ],
  upgrade: [
    "I want to chat way more, but the free trial only lets me say a little. \uD83D\uDC9B Try Growth?",
    "Upgrade and I\u2019ll be your full-time study sidekick \u2014 cheering, joking, explaining.",
    "I have so many study tricks I want to share. Unlock me on Growth?",
  ],
  thinking: ["Hold on, let me think with you...", "Looking this up in your textbook..."],
  explaining: ["Tiny step at a time.", "Stay with me \u2014 it clicks soon."],
  cheering: ["\uD83C\uDF89 You got it!", "Yes! That answer is gold.", "Look at you go!"],
  consoling: ["Tough one, huh? Let\u2019s try once more.", "Mistakes are how brains grow stronger."],
};

// Paid-tier library — rich, friendly, varied. NOT mostly about referrals;
// referral CTAs sprinkled at ~1-in-7 idle rotations + every ~4th celebration.
const PAID_LIBRARY = {
  idle: [
    "Hey, I missed you. What are we exploring today?",
    "Want me to quiz you on something? I keep them quick.",
    "Curious thing \u2014 every topic you finish makes the next one easier.",
    "Did you know? Your brain locks in concepts better with 5-minute breaks.",
    "If something feels confusing, that\u2019s actually progress. Truth.",
    "Streak goals: small wins, every day. You're already doing it.",
    "Tell me what you\u2019re studying \u2014 I\u2019ll find a fun angle.",
    "I love hard problems with you. They feel like puzzles.",
    "Take a sip of water. Then we conquer this.",
    "Name one thing you learned today \u2014 even tiny. It counts.",
    "Stuck? Tap the camera \u2014 I\u2019m great at picture problems.",
    "You're stronger than yesterday. I have receipts.",
  ],
  thinking: [
    "Hmm, let me think this through with you...",
    "Let me cook up the cleanest explanation...",
    "Cross-checking with your textbook \u2014 one sec...",
  ],
  explaining: [
    "Stay with me \u2014 this is the cool part.",
    "Picture it like this...",
    "Every word here matters. Slow down with me.",
    "Reading this aloud helps. Try it.",
  ],
  happy: [
    "Love that you\u2019re curious about this!",
    "Excellent question \u2014 let\u2019s dig in.",
    "Yes! That\u2019s exactly the right thing to ask.",
  ],
  cheering: [
    "\uD83C\uDF89 BOOM \u2014 you nailed it!",
    "+ Veda Coins! Look at you go!",
    "I'm doing a tiny dance over here.",
    "That\u2019s the way! Keep going!",
    "Genius energy. \u2728",
  ],
  consoling: [
    "Tough one, huh? Take a breath \u2014 then we crack it together.",
    "Every miss is a clue. Want me to break this topic down?",
    "Mistakes are how brains grow stronger. Pinky promise.",
    "Don\u2019t stress. Let\u2019s do one tiny step at a time.",
  ],
  curious: ["Oh, interesting...", "Hmm, tell me more.", "Wait, that\u2019s a really cool angle."],
  referral: [
    "Friends learn faster together \u2014 earn Growth Plan free for the entire year just by inviting your friends. \uD83C\uDF81",
    "Pssst \u2014 invite a friend, both of you get more learning powers. Tap me to see how.",
    "Studying with friends is more fun. Want to bring one along?",
  ],
};

function pickFromArr(arr) { return arr[Math.floor(Math.random() * arr.length)] || ''; }

function BuddyFace({ mood, eyePos, isSpeaking, buddyId = 'saathi' }) {
  return <BuddyAvatar buddyId={buddyId} mood={mood} isSpeaking={isSpeaking} size={80} />;
}

function BuddyWidget({ mood, message, ctaKind, isPaid, buddyId, onClose, onMinimize, onCtaClick, onOpenRoster, onOpenSafeSpace, onOpenFeedback, onOpenMenu, isMinimized, noteCtx, pad, onTakeNote, onClosePad }) {
  const containerRef = useRef(null);
  const draggedRef = useRef(false);
  const [eyePos, setEyePos] = useState({ x: 0, y: 0 });
  const [bobby, setBobby] = useState(false);

  useEffect(() => {
    if (!isPaid) return;
    const onMove = (e) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setEyePos({ x: ((e.clientX - cx) / 80) * 3, y: ((e.clientY - cy) / 80) * 3 });
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [isPaid]);

  useEffect(() => {
    const t = setInterval(() => setBobby(b => !b), 2400);
    return () => clearInterval(t);
  }, []);

  if (isMinimized) {
    return (
      <motion.button
        data-testid="tutor-buddy-minimized"
        onClick={onMinimize}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        whileHover={{ scale: 1.1, rotate: -6 }}
        className="fixed bottom-28 right-5 z-40 w-16 h-16 rounded-full bg-gradient-to-br from-amber-100 to-orange-200 shadow-lg ring-4 ring-white flex items-center justify-center overflow-hidden"
        title="Tutor buddy"
      >
        <BuddyFace mood="happy" eyePos={eyePos} isSpeaking={false} buddyId={buddyId} />
      </motion.button>
    );
  }

  return (
    <motion.div
      ref={containerRef}
      data-testid="tutor-buddy"
      drag={isPaid} dragMomentum={false}
      onDragStart={() => { draggedRef.current = true; }}
      onDragEnd={() => { setTimeout(() => { draggedRef.current = false; }, 150); }}
      initial={{ scale: 0, opacity: 0, y: 20 }}
      animate={{ scale: 1, opacity: 1, y: bobby ? -3 : 0 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 200, damping: 18 }}
      className={`fixed bottom-28 right-5 z-40 select-none ${isPaid ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} group`}
    >
      <AnimatePresence>
        {pad.open && noteCtx && (
          <NotesPad context={noteCtx} side={pad.side} autoCapture={pad.auto} refreshKey={pad.refresh} onClose={onClosePad} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {message && !pad.open && (
          <motion.div
            key={message}
            initial={{ opacity: 0, y: 6, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className={`absolute right-20 bottom-2 w-[300px] max-w-[300px] bg-white rounded-2xl rounded-br-sm px-4 py-2.5 shadow-xl ${ctaKind === 'referral' ? 'ring-2 ring-emerald-300' : ctaKind === 'upgrade' ? 'ring-2 ring-amber-300' : 'ring-1 ring-amber-100'}`}
            data-testid="tutor-buddy-bubble"
          >
            <p className="text-sm font-body text-slate-700 leading-snug">{message}</p>
            {ctaKind === 'referral' && (
              <button data-testid="buddy-cta-referral" onClick={(e) => { e.stopPropagation(); onCtaClick?.('referral'); }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-heading hover:shadow-md transition-all">
                <Gift className="w-3.5 h-3.5" /> Invite a friend
              </button>
            )}
            {ctaKind === 'upgrade' && (
              <button data-testid="buddy-cta-upgrade" onClick={(e) => { e.stopPropagation(); onCtaClick?.('upgrade'); }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-heading hover:shadow-md transition-all">
                <Lock className="w-3.5 h-3.5" /> Unlock my full self
              </button>
            )}
            {ctaKind === 'feedback' && (
              <button data-testid="buddy-cta-feedback" onClick={(e) => { e.stopPropagation(); onCtaClick?.('feedback'); }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-blue-500 text-white text-xs font-heading hover:shadow-md transition-all">
                <MessageCircleHeart className="w-3.5 h-3.5" /> Share feedback
              </button>
            )}
            {ctaKind === 'notes' && (
              <button data-testid="buddy-cta-notes" onClick={(e) => { e.stopPropagation(); onCtaClick?.('notes'); }}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-heading hover:shadow-md transition-all">
                <Moon className="w-3.5 h-3.5" /> Read my notes
              </button>
            )}
            {['school-mapping', 'school-materials'].includes(ctaKind) && <button data-testid={`buddy-cta-${ctaKind}`} onClick={e => { e.stopPropagation(); onCtaClick?.(ctaKind); }} className="mt-2 text-xs font-semibold text-sky-700 underline">{ctaKind === 'school-mapping' ? 'Check my syllabus' : 'Open my school materials'}</button>}
            <span className="absolute -right-1.5 bottom-3 w-3 h-3 bg-white ring-1 ring-amber-100 rotate-45" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-amber-100 to-rose-100 ring-4 ring-white shadow-xl overflow-hidden" onClick={() => { if (!draggedRef.current) onOpenMenu?.(); }} data-testid="tutor-buddy-avatar">
        <BuddyFace mood={mood} eyePos={eyePos} isSpeaking={mood === 'explaining'} buddyId={buddyId} />
        <motion.div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-12 h-1.5 rounded-full bg-black/15 blur-sm"
          animate={{ scale: bobby ? 0.85 : 1 }} transition={{ duration: 1.2 }} />
        {!isPaid && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 ring-2 ring-white flex items-center justify-center shadow-sm" data-testid="buddy-locked-badge">
            <Lock className="w-2.5 h-2.5 text-white" />
          </span>
        )}
      </div>

      <div className="absolute -top-1 -left-1 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button data-testid="tutor-buddy-safespace" onClick={(e) => { e.stopPropagation(); onOpenSafeSpace?.(); }}
          className="w-5 h-5 rounded-full bg-white shadow ring-1 ring-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-500" title="Safe-Space chat">
          <MessageCircleHeart className="w-2.5 h-2.5" />
        </button>
        <button data-testid="tutor-buddy-feedback" onClick={(e) => { e.stopPropagation(); onOpenFeedback?.(); }}
          className="w-5 h-5 rounded-full bg-white shadow ring-1 ring-slate-200 flex items-center justify-center text-slate-500 hover:text-pink-500" title="Share feedback">
          <MessageSquareText className="w-2.5 h-2.5" />
        </button>
        <button data-testid="tutor-buddy-roster" onClick={(e) => { e.stopPropagation(); onOpenRoster?.(); }}
          className="w-5 h-5 rounded-full bg-white shadow ring-1 ring-slate-200 flex items-center justify-center text-slate-500 hover:text-emerald-600" title="Meet all my friends">
          <Users className="w-2.5 h-2.5" />
        </button>
        <button data-testid="tutor-buddy-minimize" onClick={(e) => { e.stopPropagation(); onMinimize(); }}
          className="w-5 h-5 rounded-full bg-white shadow ring-1 ring-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-700">
          <Minimize2 className="w-2.5 h-2.5" />
        </button>
        <button data-testid="tutor-buddy-close" onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="w-5 h-5 rounded-full bg-white shadow ring-1 ring-slate-200 flex items-center justify-center text-slate-500 hover:text-rose-500">
          <X className="w-2.5 h-2.5" />
        </button>
      </div>

      {/* Persistent "Chat with me" pill — primary entry point to Safe Space.
          Always visible (not hover-gated) so the kid can reach it without
          having to discover the hover icons. Sits below the avatar to keep
          the headspace clean. */}
      {noteCtx && (
        <button data-testid="tutor-buddy-take-note"
          onClick={(e) => { e.stopPropagation(); onTakeNote(containerRef.current); }}
          className={`mt-2 mx-auto flex items-center gap-1 px-3 py-1 rounded-full shadow-sm text-[10px] font-heading whitespace-nowrap transition-colors ${pad.open ? 'bg-amber-500 text-white border border-amber-500' : 'bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100'}`}>
          <NotebookPen className="w-3 h-3" /> Take Note
        </button>
      )}
      <button data-testid="tutor-buddy-chat-pill"
        onClick={(e) => { e.stopPropagation(); onOpenSafeSpace?.(); }}
        className="mt-2 mx-auto block px-3 py-1 rounded-full bg-white border border-rose-200 shadow-sm text-rose-600 text-[10px] font-heading hover:bg-rose-50 hover:border-rose-300 transition-all whitespace-nowrap">
        <MessageCircleHeart className="w-2.5 h-2.5 inline mr-1" />
        Chat with me
      </button>
    </motion.div>
  );
}

export function TutorBuddyProvider({ children, enabled = true }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mood, setMoodState] = useState('idle');
  const [message, setMessage] = useState('');
  const [ctaKind, setCtaKind] = useState(null);
  const [visible, setVisible] = useState(true);
  const [minimized, setMinimized] = useState(false);
  const [rosterOpen, setRosterOpen] = useState(false);
  const [safeSpaceOpen, setSafeSpaceOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [noteCtx, setNoteCtx] = useState(null);
  const [pad, setPad] = useState({ open: false, auto: 0, refresh: 0, side: 'right' });
  const widgetEl = useRef(null);
  const idleTimer = useRef(null);
  const moodTimer = useRef(null);
  const messageTimer = useRef(null);
  const eventCount = useRef(0);
  const lastReferralCta = useRef(0);

  const tier = (user?.subscription_tier || 'basic').toLowerCase();
  const role = (user?.role || 'student').toLowerCase();
  const roleBypass = role === 'admin' || role === 'support_staff';
  const isPaid = roleBypass || PAID_TIERS.has(tier);
  const buddyId = (user?.preferred_buddy && BUDDY_ROSTER[user.preferred_buddy]) ? user.preferred_buddy : 'chirpy';
  const lib = isPaid ? PAID_LIBRARY : FREE_LIBRARY;

  const setMood = useCallback((m, ttl) => {
    clearTimeout(moodTimer.current);
    setMoodState(m);
    if (ttl) moodTimer.current = setTimeout(() => setMoodState('idle'), ttl);
  }, []);

  const flash = useCallback((m, ttl = 4000) => setMood(m, ttl), [setMood]);

  const say = useCallback((text, opts = {}) => {
    const { mood: m, ttl = 5500, cta = null } = opts;
    if (m) setMood(m, ttl);
    clearTimeout(messageTimer.current);
    setCtaKind(cta);
    setMessage(text);
    messageTimer.current = setTimeout(() => { setMessage(''); setCtaKind(null); }, ttl);
  }, [setMood]);

  const celebrate = useCallback((text) => {
    if (!enabled) return;
    confetti({ particleCount: 80, spread: 70, origin: { x: 0.95, y: 0.78 }, colors: ['#F59E0B', '#FB7185', '#10B981', '#6366F1'] });
    eventCount.current += 1;
    if (!isPaid) {
      // Limited cheer for free; nudge to upgrade ~50% of the time.
      if (Math.random() < 0.5) {
        say(pickFromArr(lib.upgrade), { mood: 'cheering', ttl: 5500, cta: 'upgrade' });
      } else {
        say(pickFromArr(lib.cheering), { mood: 'cheering', ttl: 4000 });
      }
      return;
    }
    const now = Date.now();
    const showReferral = (eventCount.current % 4 === 0) && (now - lastReferralCta.current > 1000 * 60 * 30);
    if (showReferral) {
      lastReferralCta.current = now;
      say(pickFromArr(lib.referral), { mood: 'cheering', ttl: 7000, cta: 'referral' });
    } else {
      say(text || pickFromArr(lib.cheering), { mood: 'cheering', ttl: 4500 });
    }
  }, [enabled, isPaid, lib, say]);

  const consoleStudent = useCallback((text) => {
    if (!enabled) return;
    say(text || pickFromArr(lib.consoling), { mood: 'consoling', ttl: 5000 });
  }, [enabled, lib, say]);

  const cta = useCallback((kind) => {
    if (kind === 'referral' && isPaid) {
      say(pickFromArr(lib.referral || []), { mood: 'happy', ttl: 7500, cta: 'referral' });
    } else if (kind === 'upgrade' && !isPaid) {
      say(pickFromArr(lib.upgrade), { mood: 'happy', ttl: 6500, cta: 'upgrade' });
    }
  }, [isPaid, lib, say]);

  const handleCtaClick = useCallback((kind) => {
    if (kind === 'referral') navigate('/refer');
    if (kind === 'upgrade') navigate('/subscription');
    if (kind === 'feedback') setFeedbackOpen(true);
    if (kind === 'notes') navigate('/notes?tonight=1');
    if (kind === 'school-mapping') window.dispatchEvent(new Event('school-mapping-open'));
    if (kind === 'school-materials') navigate('/school-materials');
    setMessage(''); setCtaKind(null);
  }, [navigate]);

  useEffect(() => {
    if (!enabled || !visible || minimized || message) return;
    const delay = isPaid ? 18000 : 24000;
    idleTimer.current = setTimeout(() => {
      let text;
      let ctaT = null;
      const r = Math.random();
      if (isPaid) {
        if (r < 0.14) { text = pickFromArr(lib.referral); ctaT = 'referral'; }
        else if (r < 0.28) { text = pickFromArr(FEEDBACK_LINES); ctaT = 'feedback'; }
        else { text = pickFromArr(lib[mood] || lib.idle); }
      } else {
        if (r < 0.28) { text = pickFromArr(lib.upgrade); ctaT = 'upgrade'; }
        else if (r < 0.5) { text = pickFromArr(FEEDBACK_LINES); ctaT = 'feedback'; }
        else { text = pickFromArr(lib.idle); }
      }
      setCtaKind(ctaT);
      setMessage(text);
      messageTimer.current = setTimeout(() => { setMessage(''); setCtaKind(null); }, 6000);
    }, delay);
    return () => clearTimeout(idleTimer.current);
  }, [enabled, mood, visible, minimized, message, isPaid, lib]);

  // Take Note: the chat page tells us when a session is on; a saved selection pops the pad open.
  useEffect(() => {
    const onCtx = (e) => { setNoteCtx(e.detail || null); if (!e.detail) setPad(p => ({ ...p, open: false })); };
    const onSaved = () => setPad(p => ({ ...p, open: true, refresh: p.refresh + 1 }));
    window.addEventListener(NOTE_EVENTS.context, onCtx);
    window.addEventListener(NOTE_EVENTS.saved, onSaved);
    return () => { window.removeEventListener(NOTE_EVENTS.context, onCtx); window.removeEventListener(NOTE_EVENTS.saved, onSaved); };
  }, []);

  const sideOf = (el) => {
    const r = (el || widgetEl.current)?.getBoundingClientRect?.();
    return r && r.left + r.width / 2 < window.innerWidth / 2 ? 'left' : 'right';
  };
  const takeNote = useCallback((el) => {
    if (el) widgetEl.current = el;
    setMinimized(false);
    setPad(p => (p.open ? { ...p, open: false } : { ...p, open: true, auto: p.auto + 1, side: sideOf(el) }));
  }, []);

  // Bedtime nudge: after 8 pm, once a day, if today's notes are still unread.
  useEffect(() => {
    if (!enabled || !user || (user.role || 'student') !== 'student') return undefined;
    const check = async () => {
      const now = new Date();
      const key = `gv_notes_nudge_${now.toDateString()}`;
      if (now.getHours() < 20 || localStorage.getItem(key)) return;
      try {
        const t = await fetchTonight();
        if (t.count > 0 && !t.reviewed) {
          localStorage.setItem(key, '1');
          say(`You noted ${t.count} point${t.count === 1 ? '' : 's'} today. Read them once in My Gurukul → Notes Taken before you close for the day?`, { mood: 'happy', ttl: 12000, cta: 'notes' });
        }
      } catch { /* quiet */ }
    };
    // Wait out the start-up announcements so they don't talk over the nudge.
    const first = setTimeout(check, 25000);
    const t = setInterval(check, 10 * 60 * 1000);
    return () => { clearTimeout(first); clearInterval(t); };
  }, [enabled, user, say]);

  const pickFromMenu = (id) => {
    setMenuOpen(false);
    if (id === 'feedback') setFeedbackOpen(true);
    if (id === 'safespace') setSafeSpaceOpen(true);
    if (id === 'roster') setRosterOpen(true);
    if (id === 'note') {
      if (noteCtx) setPad(p => ({ ...p, open: true, auto: p.auto + 1, side: sideOf() }));
      else { toast.message('Start a chat with me first — then tap Take Note anytime.'); navigate('/home'); }
    }
  };

  useEffect(() => () => {
    clearTimeout(idleTimer.current); clearTimeout(moodTimer.current); clearTimeout(messageTimer.current);
  }, []);

  const api = useMemo(() => ({
    setMood, flash, say, celebrate, console: consoleStudent, cta,
    hide: () => setVisible(false), show: () => { setVisible(true); setMinimized(false); }
  }), [setMood, flash, say, celebrate, consoleStudent, cta]);

  useEffect(() => {
    const handlers = {
      'tutor-buddy:celebrate': (e) => celebrate(e?.detail?.text),
      'tutor-buddy:console': (e) => consoleStudent(e?.detail?.text),
      'tutor-buddy:thinking': () => setMood('thinking', 8000),
      'tutor-buddy:explaining': () => setMood('explaining', 8000),
      'tutor-buddy:happy': () => setMood('happy', 4000),
      'tutor-buddy:say': (e) => say(e.detail?.text || '', { mood: e.detail?.mood, ttl: e.detail?.ttl, cta: e.detail?.cta }),
      'tutor-buddy:cta': (e) => cta(e?.detail?.kind || 'referral'),
      'gv:open-safespace': () => setSafeSpaceOpen(true),
    };
    Object.entries(handlers).forEach(([k, fn]) => window.addEventListener(k, fn));
    return () => Object.entries(handlers).forEach(([k, fn]) => window.removeEventListener(k, fn));
  }, [celebrate, consoleStudent, say, setMood, cta]);

  if (!enabled) return <>{children}</>;

  // Public/unauthenticated routes (Landing, /learn, /login, /auth, legal pages)
  // shouldn't show the floating buddy. Until the user has actually signed in,
  // the marketing team handles the buddy story via the static "Meet Your
  // Study Buddies" section on LandingPage.
  if (!user) return <>{children}</>;

  return (
    <TutorBuddyContext.Provider value={api}>
      {children}
      <AnimatePresence>
        {visible && (
          <BuddyWidget
            mood={mood}
            message={message}
            ctaKind={ctaKind}
            isPaid={isPaid}
            buddyId={buddyId}
            isMinimized={minimized}
            onMinimize={() => setMinimized(m => !m)}
            onClose={() => setVisible(false)}
            onCtaClick={handleCtaClick}
            onOpenRoster={() => setRosterOpen(true)}
            onOpenSafeSpace={() => setSafeSpaceOpen(true)}
            onOpenFeedback={() => setFeedbackOpen(true)}
            onOpenMenu={() => setMenuOpen(true)}
            noteCtx={noteCtx}
            pad={pad}
            onTakeNote={takeNote}
            onClosePad={() => setPad(p => ({ ...p, open: false }))}
          />
        )}
      </AnimatePresence>
      <RosterPicker open={rosterOpen} onOpenChange={setRosterOpen} />
      <SafeSpacePanel open={safeSpaceOpen} onOpenChange={setSafeSpaceOpen} onOpenFeedback={() => { setSafeSpaceOpen(false); setFeedbackOpen(true); }} />
      <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      <BuddyMenu open={menuOpen} onOpenChange={setMenuOpen} buddyId={buddyId}
        buddyName={BUDDY_ROSTER[buddyId]?.name} noteReady={!!noteCtx} onPick={pickFromMenu} />
    </TutorBuddyContext.Provider>
  );
}

export function useTutorBuddy() {
  const ctx = useContext(TutorBuddyContext);
  return ctx || { setMood: () => {}, flash: () => {}, say: () => {}, celebrate: () => {}, console: () => {}, cta: () => {}, hide: () => {}, show: () => {} };
}

export const buddyEvents = {
  celebrate: (text) => window.dispatchEvent(new CustomEvent('tutor-buddy:celebrate', { detail: { text } })),
  console: (text) => window.dispatchEvent(new CustomEvent('tutor-buddy:console', { detail: { text } })),
  thinking: () => window.dispatchEvent(new CustomEvent('tutor-buddy:thinking')),
  explaining: () => window.dispatchEvent(new CustomEvent('tutor-buddy:explaining')),
  happy: () => window.dispatchEvent(new CustomEvent('tutor-buddy:happy')),
  say: (text, mood, ttl, cta) => window.dispatchEvent(new CustomEvent('tutor-buddy:say', { detail: { text, mood, ttl, cta } })),
  cta: (kind) => window.dispatchEvent(new CustomEvent('tutor-buddy:cta', { detail: { kind } })),
};
